import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, AlertTriangle } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const WhyNeedSection = ({ onOpenContactForm }) => {
  const section = useScrollAnimation({ threshold: 0.15 });

  const risks = [
    "Competitors register your brand name first",
    "Counterfeit sellers steal your product listings",
    "You can't access Amazon Brand Registry",
    "No legal standing in federal court",
    "Years of brand equity wiped out overnight"
  ];

  return (
    <section data-testid="why-need-section" ref={section.ref} className="py-24 md:py-32 bg-[#0A0A12] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500/20 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-stretch">
          {/* Image */}
          <div className={`lg:w-[45%] w-full order-1 scroll-slide-left ${section.isVisible ? 'scroll-visible' : ''}`}>
            <div className="relative rounded-xl overflow-hidden h-full min-h-[400px] border border-white/5">
              <img
                src="https://images.unsplash.com/photo-1771442062904-44dddcea7fcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnbGFzcyUyMGJ1aWxkaW5nJTIwYXJjaGl0ZWN0dXJlJTIwbmlnaHQlMjBibHVlfGVufDB8fHx8MTc3NTI0NjkyN3ww&ixlib=rb-4.1.0&q=85"
                alt="Corporate building illuminated at night"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A12]/80 via-[#0A0A12]/30 to-transparent" />
              <div className="absolute bottom-8 left-8 right-8">
                <div className="backdrop-blur-xl bg-black/50 border border-white/10 rounded-lg p-5">
                  <div className="text-3xl font-black text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>30%+</div>
                  <p className="text-sm text-slate-400">of self-filed trademark applications get rejected by the USPTO</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={`lg:w-[55%] w-full order-2 scroll-slide-right scroll-delay-2 ${section.isVisible ? 'scroll-visible' : ''}`}>
            <span className="text-xs tracking-[0.2em] uppercase text-amber-500 mb-4 block font-bold">The Cost of Doing Nothing</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter text-white leading-[0.95] mb-6 uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
              What Happens When You <span className="text-gradient-amber">Don't Register</span>
            </h2>
            <p className="text-base text-slate-300 leading-relaxed mb-8">
              Without federal trademark registration, your brand has zero legal protection outside your state. Anyone can copy your name, sell under your brand, and there's nothing you can do about it. The USPTO won't protect you unless you file.
            </p>

            <div className="space-y-4 mb-10">
              {risks.map((risk, i) => (
                <div key={i} className="flex items-start gap-3 group">
                  <div className="w-6 h-6 mt-0.5 rounded-sm bg-red-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-red-500/25 transition-colors">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                  </div>
                  <span className="text-sm text-slate-300 group-hover:text-white transition-colors">{risk}</span>
                </div>
              ))}
            </div>

            <Button
              data-testid="why-need-cta"
              onClick={onOpenContactForm}
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold px-8 py-5 text-sm rounded-md uppercase tracking-wider btn-shine shadow-lg shadow-amber-500/20"
            >
              Protect My Brand Now
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyNeedSection;
